import {
  checkoutAction,
  fetchCheckoutMetaAction,
  fetchCheckoutPaymentMethodsAction,
  getListOfVouchersAction,
  removeSelectedVoucherAction,
  selectSavedCreditCardAction,
  setSelectedVoucherAction,
  storeCheckoutAddressAction,
  unselectSavedCreditCardAction,
  updateCheckoutMetaAction,
  updateDeliveryDetailsAction,
  validateVoucherAction,
  retrieveCheckoutAddressAction
} from './action'
import { updateUserAction } from 'src/redux/user/action'
import { createSlice } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'

const initialState: Oms.V4.Checkout = {
  meta: {
    status: 'none',
    error: null,
    data: {} as Oms.V4.CheckoutMetaData
  },
  paymentMethods: {
    status: 'none',
    data: []
  },
  checkoutResults: {
    status: 'none',
    data: {} as {
      storeOrder: Oms.V4.StoreOrder
    }
  },
  paymentToken: {
    status: 'none',
    data: null
  },
  vouchers: {
    status: 'none',
    data: []
  },
  selectedVoucher: {
    status: 'none',
    data: {} as Oms.V4.SelectedVoucher
  },
  checkoutAddressInfo: {
    status: 'none',
    data: {}
  },
  selectedSavedCreditCard: {
    status: 'none',
    data: {} as Oms.V4.SelectedSavedCreditCard
  }
}

export type CheckoutStateInReducer = {
  '@checkout': Oms.V4.Checkout
}

export const checkoutSlice = createSlice({
  name: '@checkout',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchCheckoutMetaAction.pending, (state) => {
        state.meta.status = 'pending'
      })
      .addCase(fetchCheckoutMetaAction.fulfilled, (state, action) => {
        state.meta.status = 'fulfilled'
        state.meta.data = action.payload?.data || {}
        if (action.payload?.data?.storeOrder?.orderVouchers?.[0]) {
          state.selectedVoucher.status = 'fulfilled'
          state.selectedVoucher.data =
            action.payload.data.storeOrder.orderVouchers[0]
        }
      })
      .addCase(fetchCheckoutMetaAction.rejected, (state, action) => {
        state.meta.status = 'rejected'
        state.meta.error = [{ errorCode: action.error.message }]
        const payloadResponse = action.payload as {
          errors: {
            errorCode: string
          }[]
        }
        if (payloadResponse?.errors) {
          state.meta.error = payloadResponse.errors
        }
      })
      .addCase(fetchCheckoutPaymentMethodsAction.pending, (state) => {
        state.paymentMethods.status = 'pending'
      })
      .addCase(fetchCheckoutPaymentMethodsAction.fulfilled, (state, action) => {
        state.paymentMethods.status = 'fulfilled'
        state.paymentMethods.data =
          action.payload?.payload?.paymentOptions || []
      })
      .addCase(fetchCheckoutPaymentMethodsAction.rejected, (state) => {
        state.paymentMethods.status = 'rejected'
        // state.meta.error = action.payload.error
      })
      .addCase(checkoutAction.pending, (state) => {
        state.checkoutResults.status = 'pending'
      })
      .addCase(checkoutAction.fulfilled, (state, action) => {
        state.checkoutResults.status = 'fulfilled'
        state.checkoutResults.data = action.payload?.result || {}
      })
      .addCase(checkoutAction.rejected, (state) => {
        state.checkoutResults.status = 'rejected'
        // state.meta.error = action.payload.error
      })
      .addCase(getListOfVouchersAction.pending, (state) => {
        state.vouchers.status = 'pending'
      })
      .addCase(getListOfVouchersAction.fulfilled, (state, action) => {
        state.vouchers.status = 'fulfilled'
        state.vouchers.data = action.payload.data
      })
      .addCase(getListOfVouchersAction.rejected, (state, action) => {
        state.vouchers.status = 'rejected'
        state.vouchers.error = action.error.message
      })
      .addCase(updateDeliveryDetailsAction.fulfilled, (state, action) => {
        state.vouchers.status = 'fulfilled'
        state.meta.data = {
          ...state.meta.data,
          storeOrder: action.payload.data
        }
      })
      .addCase(removeSelectedVoucherAction.fulfilled, (state) => {
        state.selectedVoucher.data = {} as Oms.V4.SelectedVoucher
      })
      .addCase(setSelectedVoucherAction, (state, action) => {
        state.selectedVoucher.data =
          action.payload || ({} as Oms.V4.SelectedVoucher)
      })
      .addCase(validateVoucherAction.pending, (state) => {
        state.selectedVoucher.status = 'pending'
      })
      .addCase(validateVoucherAction.fulfilled, (state, action) => {
        state.selectedVoucher.status = 'fulfilled'
        const selectedVoucherInfo = (state.vouchers.data || []).find(
          (voucher) => voucher.code === action.payload.data.code
        )

        state.selectedVoucher.data = {
          ...(action.payload.data || {}),
          ...(selectedVoucherInfo || {})
        }
      })
      .addCase(validateVoucherAction.rejected, (state, action) => {
        state.selectedVoucher.status = 'rejected'
        state.selectedVoucher.error = action.error.message
      })
      .addCase(storeCheckoutAddressAction, (state, action) => {
        state.checkoutAddressInfo.data = action.payload
      })
      .addCase(selectSavedCreditCardAction, (state, action) => {
        state.selectedSavedCreditCard.selected = true
        state.selectedSavedCreditCard.data = action.payload
      })
      .addCase(unselectSavedCreditCardAction, (state) => {
        state.selectedSavedCreditCard.selected = false
      })
      .addCase(updateCheckoutMetaAction.rejected, (state, action) => {
        state.meta.status = 'rejected'
        state.meta.error = [{ errorCode: action.error.message }]
        const payloadResponse = action.payload as {
          error: {
            errorCode: string
          }[]
        }
        if (payloadResponse?.error) {
          state.meta.error = payloadResponse.error
        }
      })
      .addCase(updateCheckoutMetaAction.fulfilled, (state, action) => {
        state.meta.status = 'fulfilled'
        state.meta.data = {
          ...state.meta.data,
          ...(action.payload?.data || {})
        }
      })
      .addCase(updateCheckoutMetaAction.pending, (state) => {
        state.meta.status = 'pending'
      })
      .addCase(updateUserAction.rejected, (state) => {
        state.meta.status = 'rejected'
      })
      .addCase(updateUserAction.pending, (state) => {
        state.meta.status = 'pending'
      })
      .addCase(updateUserAction.fulfilled, (state, action) => {
        state.meta.status = 'fulfilled'
        state.meta.data = {
          ...state.meta.data,
          customer: action.payload.payload
        }
      })
      .addCase(retrieveCheckoutAddressAction, (state, action) => {
        if (action.payload && state.meta.data.storeOrder) {
          state.meta.data.storeOrder.cachedCheckoutAddress = action.payload
        }
      })
      .addCase(HYDRATE, (state, action) => ({
        ...state,
        ...action['payload']['@checkout']
      }))
})
